// src/hooks/useContactInfo.ts
// Hook untuk mengambil data informasi kontak dari database

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export interface ContactInfo {
  id: string;
  profile_id: string;
  email?: string;
  phone?: string;
  address?: string;
  working_hours?: string;
  response_time?: string;
  social_links?: Record<string, string>;
  about_points?: string[];
  form_subjects?: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface UseContactInfoReturn {
  contactInfo: ContactInfo | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useContactInfo = (): UseContactInfoReturn => {
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContactInfo = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('contact_info')
        .select('*')
        .eq('is_active', true)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (data) {
        setContactInfo(data);
      } else {
        // Fallback to default contact info if no data found
        const defaultContactInfo: ContactInfo = {
          id: 'default',
          profile_id: 'default',
          email: 'contact@example.com',
          phone: '+62 812-3456-7890',
          address: 'Jakarta, Indonesia',
          working_hours: 'Senin - Jumat, 09:00 - 17:00',
          response_time: 'Respon dalam 24 jam',
          social_links: {
            'LinkedIn': 'https://linkedin.com/in/yourprofile',
            'Instagram': 'https://instagram.com/yourprofile'
          },
          about_points: [
            'Berpengalaman dalam bidang pendidikan anak',
            'Memiliki passion dalam pengembangan materi pembelajaran',
            'Aktif dalam komunitas pendidik Indonesia',
            'Tersedia untuk konsultasi dan kolaborasi'
          ],
          form_subjects: [
            'Konsultasi Umum',
            'Kerjasama Proyek',
            'Permintaan Materi Khusus',
            'Feedback dan Saran',
            'Lainnya'
          ],
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setContactInfo(defaultContactInfo);
      }
    } catch (err) {
      console.error('Error fetching contact info:', err);
      setError(err instanceof Error ? err.message : 'Gagal memuat informasi kontak');
      
      // Fallback to default contact info on error
      const defaultContactInfo: ContactInfo = {
        id: 'default',
        profile_id: 'default',
        email: 'contact@example.com',
        phone: '+62 812-3456-7890',
        address: 'Jakarta, Indonesia',
        working_hours: 'Senin - Jumat, 09:00 - 17:00',
        response_time: 'Respon dalam 24 jam',
        social_links: {
          'LinkedIn': 'https://linkedin.com/in/yourprofile',
          'Instagram': 'https://instagram.com/yourprofile'
        },
        about_points: [
          'Berpengalaman dalam bidang pendidikan anak',
          'Memiliki passion dalam pengembangan materi pembelajaran',
          'Aktif dalam komunitas pendidik Indonesia',
          'Tersedia untuk konsultasi dan kolaborasi'
        ],
        form_subjects: [
          'Konsultasi Umum',
          'Kerjasama Proyek',
          'Permintaan Materi Khusus',
          'Feedback dan Saran',
          'Lainnya'
        ],
        is_active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setContactInfo(defaultContactInfo);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const refetch = async () => {
    await fetchContactInfo();
  };

  return {
    contactInfo,
    loading,
    error,
    refetch
  };
};

export default useContactInfo;